import React, { useEffect, useState } from 'react';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Container,
  Card, CardContent, CardActionArea, Grid, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Chip, Alert, Menu, MenuItem, Divider,
} from '@mui/material';
import {
  Add, Logout, Park, AccountCircle, ManageAccounts,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projekteAPI } from '../services/api';

export default function ProjektListe() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projekte, setProjekte] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', kunde: '', eigentuemer: '', bemerkung: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await projekteAPI.list();
      setProjekte(list);
    } catch (e) {
      setError('Projekte konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) { setError('Name erforderlich'); return; }
    setBusy(true); setError('');
    try {
      const projekt = await projekteAPI.create(form);
      setDialogOpen(false);
      setForm({ name: '', kunde: '', eigentuemer: '', bemerkung: '' });
      navigate(`/projekt/${projekt.id}`);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Anlegen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          <Park sx={{ mr: 1.5 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Baumwächter · Erfassung
          </Typography>
          <IconButton color="inherit" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Stack>
                <Typography variant="body2" fontWeight={600}>{user?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                <Chip
                  size="small"
                  label={user?.role}
                  color={user?.role === 'admin' ? 'primary' : 'default'}
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                />
              </Stack>
            </MenuItem>
            <Divider />
            {isAdmin && (
              <MenuItem onClick={() => { setMenuAnchor(null); navigate('/users'); }}>
                <ManageAccounts fontSize="small" sx={{ mr: 1 }} /> Benutzer verwalten
              </MenuItem>
            )}
            <MenuItem onClick={logout}>
              <Logout fontSize="small" sx={{ mr: 1 }} /> Abmelden
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>
            Meine Projekte
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
            Neues Projekt
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && projekte.length === 0 && (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Du hast noch keine Projekte. Lege das erste an!
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
              Erstes Projekt anlegen
            </Button>
          </Card>
        )}

        <Grid container spacing={2}>
          {projekte.map((p) => (
            <Grid item xs={12} sm={6} key={p.id}>
              <Card>
                <CardActionArea onClick={() => navigate(`/projekt/${p.id}`)}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      🌳 {p.name}
                    </Typography>
                    {p.kunde && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {p.kunde}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Chip
                        size="small"
                        label={`${p.baeume_count || 0} Bäume`}
                        color={p.baeume_count > 0 ? 'primary' : 'default'}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={new Date(p.created_at).toLocaleDateString('de-DE')}
                      />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neues Projekt</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Projekt-Name"
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="z.B. Mustergemeinde 2026"
              autoFocus
              required
            />
            <TextField
              label="Kunde"
              fullWidth
              value={form.kunde}
              onChange={(e) => setForm({ ...form, kunde: e.target.value })}
              placeholder="z.B. Stadt Mustergemeinde"
            />
            <TextField
              label="Eigentümer"
              fullWidth
              value={form.eigentuemer}
              onChange={(e) => setForm({ ...form, eigentuemer: e.target.value })}
              placeholder="für CSV-Export – Wer ist Eigentümer der Bäume?"
              helperText="Wird in jedem Baum-Datensatz als Eigentümer eingetragen"
            />
            <TextField
              label="Bemerkung"
              fullWidth
              multiline
              rows={2}
              value={form.bemerkung}
              onChange={(e) => setForm({ ...form, bemerkung: e.target.value })}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy || !form.name.trim()}>
            Anlegen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
